import React, { useState, useEffect } from 'react';
import { getBellSchedule, getCurrentPeriod } from '../utils/bellSchedule';
import './DisplayPeriodTimer.css';

const DisplayPeriodTimer = ({ scheduleType, classes, organization }) => {
    const [currentPeriod, setCurrentPeriod] = useState(null);
    const [timeLeft, setTimeLeft] = useState('');
    const [progress, setProgress] = useState(0); // 0-100 percentage

    const getClassForCurrentPeriod = (period) => {
        if (!period || !organization || !classes) return null;
        if (period.period === 'Lunch' || period.period === 'Collaboration') return null;

        const periodNum = period.period.replace('Period ', '');
        const periodKey = `period${periodNum}`;

        const assignedIds = organization[periodKey];
        if (!assignedIds || assignedIds.length === 0) return null;

        const classId = assignedIds[0];
        const googleId = classId.replace('google-', '');

        return classes.find(c =>
            c.id === googleId || c.courseId === googleId
        );
    };

    useEffect(() => {
        const updateTimer = () => {
            const schedule = getBellSchedule(scheduleType);
            if (!schedule) return;

            const period = getCurrentPeriod(schedule);
            setCurrentPeriod(period);

            if (period) {
                const now = new Date();
                const currentMinutes = now.getHours() * 60 + now.getMinutes();
                const currentSeconds = now.getSeconds();

                const [endHour, endMin] = period.end.split(':').map(Number);
                const endMinutes = endHour * 60 + endMin;

                const [startHour, startMin] = period.start.split(':').map(Number);
                const startMinutes = startHour * 60 + startMin;

                const totalDuration = endMinutes - startMinutes;
                const elapsed = currentMinutes - startMinutes;
                const remaining = endMinutes - currentMinutes;

                // Calculate progress percentage (0-100)
                const progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
                setProgress(progressPercent);

                // Calculate time left with actual seconds
                const minutesLeft = remaining - 1; // Subtract 1 because we're counting down within the current minute
                const secondsLeft = 60 - currentSeconds;

                if (remaining > 1) {
                    setTimeLeft(`${minutesLeft} min ${secondsLeft} sec left`);
                } else if (remaining === 1) {
                    setTimeLeft(`${secondsLeft} sec left`);
                } else if (remaining < 1 && secondsLeft > 0) {
                    setTimeLeft(`${secondsLeft} sec left`);
                } else {
                    setTimeLeft('Period ending soon');
                }
            } else {
                setTimeLeft('No period active');
                setProgress(0);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000); // Update every second

        return () => clearInterval(interval);
    }, [scheduleType]);

    if (!currentPeriod) {
        return (
            <div className="period-timer">
                <p>No active period</p>
            </div>
        );
    }

    // Determine progress bar color based on how much time is left
    const getProgressBarClass = () => {
        if (progress > 90) return 'progress-bar-fill warning'; // Almost done - red
        if (progress > 75) return 'progress-bar-fill caution'; // Getting close - yellow
        return 'progress-bar-fill'; // Normal - green
    };

    const classInfo = getClassForCurrentPeriod(currentPeriod);

    return (
        <div className="period-timer">
            <div className="period-info">
                <h2>
                    {classInfo
                        ? `${classInfo.name} (${currentPeriod.period})`
                        : currentPeriod.period}
                </h2>

                <p className="time-range">
                    {currentPeriod.start} - {currentPeriod.end}
                </p>

                <p className="time-left">{timeLeft}</p>
            </div>


            <div className="progress-bar-container">
                <div
                    className={getProgressBarClass()}
                    style={{ width: `${progress}%` }}
                >
                </div>
            </div>
        </div>
    );
};

export default DisplayPeriodTimer;
